import { CustomerPayment, CustomerPaymentResponse, PaymentStatementToCustomerRequest } from './customerPaymentType';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;


class CustomerPaymentApi {

    ///customer_payments/payment_statement_pdf?invoice_id=123&customer_id=456

    static async viewPaymentStatementPdf(invoiceId: string, customerId: string): Promise<Blob> {
        try {
            const response = await fetch(`${API_BASE_URL}/customer_payments/payment_statement_pdf?invoice_id=${invoiceId}&customer_id=${customerId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include', // Include cookies in the request
            });

            if (response.ok) {
                // Return the PDF blob directly
                const blob = await response.blob();
                return blob;
            }
            else if (response.status === 401) {
                throw new Error(`${response.status}: Session expired. Try to refresh your browser.`);
            }
            else {
                const errorBody = await response.text();
                let errorMessage = `Error ${response.status}: ${response.statusText}`;
                if (errorBody) {
                    errorMessage += ` - ${errorBody}`;
                }
                throw new Error(errorMessage);
            }
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to view payment statement in pdf: ${error.message}`);
            } else {
                throw new Error('Failed to view payment statement in pdf: An unknown error occurred.');
            }
        }
    }

    static async sendPaymentStatementToCustomer(paymentStatementToCustomerRequest: PaymentStatementToCustomerRequest) {
        try {
            const response = await fetch(`${API_BASE_URL}/customer_payments/send_payment_statement`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(paymentStatementToCustomerRequest),
                credentials: 'include', // Include cookies in the request
            });

            if (response.ok) {
                const data = await response.json();
                return data;
            }
            else if (response.status === 401) {
                throw new Error(`${response.status}: Session expired. Try to refresh your browser.`);
            }
            else {
                // Handle error response
                const errorBody = await response.text();
                let errorMessage = `Error ${response.status}: ${response.statusText}`;
                if (errorBody) {
                    errorMessage += ` - ${errorBody}`;
                }
                throw new Error(errorMessage);
            }
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to send payment statement: ${error.message}`);
            } else {
                throw new Error('Failed to send payment statement: An unknown error occurred.');
            }
        }
    }
    
    static async getCustomerPaymentByInvoiceId(invoice_id: string): Promise<CustomerPayment[]> {
        try {
            // Fixed: Added missing slash before invoice_id
            const response = await fetch(`${API_BASE_URL}/customer_payments/by_invoice/${invoice_id}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include', // Include cookies in the request
            });

            const responseBody = await response.json();

            if (response.status === 200) {
                // Fixed: Handle array response since the backend returns multiple payments
                const payments: CustomerPayment[] = responseBody.data.map((item: any) => ({
                    customer_payment_id: item.attributes.customer_payment_id,
                    invoice_id: item.attributes.invoice_id,
                    customer_id: item.attributes.customer_id,
                    project_id: item.attributes.project_id,
                    amount: parseFloat(item.attributes.amount), // Convert string to number
                    payment_date: item.attributes.payment_date,
                    payment_method: item.attributes.payment_method,
                    note: item.attributes.note, // Fixed: Changed from 'notes' to 'note'
                    created_at: item.attributes.created_at,
                    updated_at: item.attributes.updated_at
                }));

                return payments;
            }
            else if (response.status === 401) {
                throw new Error(`${response.status}: Session expired. Try to refresh your browser.`);
            }
            else if (response.status === 404) {
                throw new Error('No payments found for this invoice'); // Fixed error message
            } else {
                throw new Error(`${responseBody.error} status ${response.status}`);
            }
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to get customer payments: ${error.message}`); // Fixed error message
            } else {
                throw new Error('Failed to get customer payments: An unknown error occurred.');
            }
        }
    }

    // Bonus: Method to get customer payments by customer ID
    static async getCustomerPaymentByCustomerId(customer_id: string): Promise<CustomerPaymentResponse> {
        try {
            const response = await fetch(`${API_BASE_URL}/customer_payments/by_customer/${customer_id}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            const responseBody = await response.json();

            if (response.status === 200) {
                // Transform the response to match your TypeScript interfaces
                const transformedResponse: CustomerPaymentResponse = {
                    projects: responseBody.projects.map((project: any) => ({
                        project_id: project.project_id,
                        name: project.name,
                        project_description: project.project_description,
                        project_type: project.project_type,
                        is_active: project.is_active,
                        estimated_sum: project.estimated_sum,
                        estimated_duration_days: project.estimated_duration_days,
                        start_date: project.start_date,
                        end_date: project.end_date,
                        created_at: project.created_at,
                        updated_at: project.updated_at,
                        invoices: project.invoices.map((invoice: any) => ({
                            ...invoice, // Keep all invoice properties as they are
                            customer_payments: invoice.customer_payments.map((payment: any) => ({
                                customer_payment_id: payment.attributes.customer_payment_id.toString(),
                                invoice_id: payment.attributes.invoice_id.toString(),
                                customer_id: payment.attributes.customer_id.toString(),
                                project_id: payment.attributes.project_id.toString(),
                                amount: parseFloat(payment.attributes.amount),
                                payment_date: payment.attributes.payment_date,
                                payment_method: payment.attributes.payment_method,
                                note: payment.attributes.note,
                                created_at: payment.attributes.created_at,
                                updated_at: payment.attributes.updated_at
                            }))
                        }))
                    }))
                };

                return transformedResponse;
            }
            else if (response.status === 401) {
                throw new Error(`${response.status}: Session expired. Try to refresh your browser.`);
            }
            else if (response.status === 404) {
                throw new Error('No payments have been created and found for this customer. You can create a payment for an existing invoice of a project.');
            } else {
                throw new Error(`${responseBody.error} status ${response.status}`);
            }
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to get customer payments: ${error.message}`);
            } else {
                throw new Error('Failed to get customer payments: An unknown error occurred.');
            }
        }
    }

    static async updateCustomerPaymentByPaymentId(customer_payment_id: string, customer_payment: CustomerPayment) {
        try {
            const response = await fetch(`${API_BASE_URL}/customer_payments/${customer_payment_id}`, {
                method: 'PATCH',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ customer_payment: customer_payment }),
            });

            const responseBody = await response.json();

            if (response.status === 200) {
                // Successfully updated, no return value needed as per reference
            }
            else if (response.status === 401) {
                throw new Error(`${response.status}: Session expired. Try to refresh your browser.`);
            }
            else {
                throw new Error(`${responseBody.error}, status: ${response.status}`);
            }
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to update customer payment: ${error.message}`);
            } else {
                throw new Error('Failed to update customer payment: An unknown error occurred.');
            }
        }
    }

    // Bonus: Method to create a new customer payment
    static async createCustomerPayment(paymentData: Omit<CustomerPayment, 'customer_payment_id' | 'created_at' | 'updated_at'>): Promise<CustomerPayment> {
        try {
            const response = await fetch(`${API_BASE_URL}/customer_payments`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ customer_payment: paymentData })
            });

            const responseBody = await response.json();

            if (response.status === 201) {
                const item = responseBody.data.attributes;
                const customerPayment: CustomerPayment = {
                    customer_payment_id: item.customer_payment_id,
                    invoice_id: item.invoice_id,
                    customer_id: item.customer_id,
                    project_id: item.project_id,
                    amount: parseFloat(item.amount),
                    payment_date: item.payment_date,
                    payment_method: item.payment_method,
                    note: item.note,
                    created_at: item.created_at,
                    updated_at: item.updated_at
                };

                return customerPayment;
            }
            else if (response.status === 401) {
                throw new Error(`${response.status}: Session expired. Try to refresh your browser.`);
            }
            else if (response.status === 422) {
                const errorDetails = responseBody.details ? responseBody.details.join(', ') : responseBody.error;
                throw new Error(`Validation error: ${errorDetails}`);
            } else {
                throw new Error(`${responseBody.error} status ${response.status}`);
            }
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to create customer payment: ${error.message}`);
            } else {
                throw new Error('Failed to create customer payment: An unknown error occurred.');
            }
        }
    }



    static async deleteCustomerPaymentByPaymentId(payment_id: string) {
        try {
            const response = await fetch(`${API_BASE_URL}/customer_payments/${payment_id}`, {
                method: 'DELETE',
                credentials: 'include',  // If you need to send cookies
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            const responseBody = await response.json();
            //console.log('body======', response.status);

            if (response.status === 200) {
                //return company;
            }
            else if (response.status === 401) {
                throw new Error(`${response.status}: Session expired. Try to refresh your browser.`);
            }
            else {
                throw new Error(`${responseBody.error}, status: ${response.status}`);
            }
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to delete estimate: ${error.message}`);
            } else {
                throw new Error('Failed to delete estimate: An unknown error occurred.');
            }
        }
    }


}

export default CustomerPaymentApi;